import React from 'react';
import { useRouteMatch } from 'react-router';
import { useSelector } from 'hooks/store';
import SectionBasePage from 'components/SectionBasePage';
import { Table } from 'UI';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import {
  isLoadingUsersSelector,
  usersEntitiesSelector,
  usersIdsSelector,
} from 'state/slices/users';
import { UserCommon } from 'constants/interface';
import { TablePaginationConfig } from 'antd';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { PaginationInternalData } from 'hooks/pagination';

import UserListExtra from './components/UserListExtra';
import {
  MainContainer,
  DetailsButton,
} from './style';

interface UserListPageProps {
  pageData: PaginationInternalData
  onTableChange: (
    pageData: TablePaginationConfig,
    filterData: Record<string, FilterValue | null>,
    sortData: SorterResult<
    UserCommon> | SorterResult<UserCommon>[],
  ) => void
  onSearch(content: string): void
}

const UserListPage = ({
  pageData,
  onTableChange,
  onSearch,
}: UserListPageProps): JSX.Element => {
  const usersEntites = useSelector(usersEntitiesSelector);
  const usersIds = useSelector(usersIdsSelector);
  const isLoadingUsers = useSelector(isLoadingUsersSelector);
  const { url } = useRouteMatch();
  const breadcrumbNavigator = useBreadcrumbNavigation();

  const users = usersIds.map((userId) => {
    const entity = usersEntites[userId];
    if (!entity) return undefined;
    const tableUser: UserCommon & {
      key?: string,
    } = {
      ...entity,
      key: userId.toString(),
    };
    return tableUser;
  }).filter((tableUser): tableUser is UserCommon => !!tableUser);

  const sorter = (): number => 0;

  const isActiveFilter = pageData.filter.isActive ?? null;
  const isAdminFilter = pageData.filter.isAdmin ?? null;

  return (
    <SectionBasePage title="Usuários" extra={<UserListExtra onSearch={onSearch} />}>
      <MainContainer>
        <Table
          dataSource={users}
          loading={isLoadingUsers}
          rowClassName={(rowData) => (rowData.isActive ? 'active-user-row' : 'inactive-user-row')}
          pagination={pageData}
          showSorterTooltip={false}
          locale={{
            filterReset: 'Limpar',
            cancelSort: 'Clique para limpar ordenação',
            triggerAsc: 'Clique para ordem crescente',
            triggerDesc: 'Clique para ordem decrescente',
          }}
          onChange={onTableChange}
        >
          <Table.Column
            title="Nome"
            key="name"
            dataIndex="name"
            sorter={sorter}
            sortOrder={pageData.orderBy === 'name' ? pageData.order : undefined}
          />
          <Table.Column
            title="E-mail"
            key="email"
            dataIndex="email"
            sorter={sorter}
            sortOrder={pageData.orderBy === 'email' ? pageData.order : undefined}
          />
          <Table.Column
            title="Ativo"
            key="isActive"
            dataIndex="isActive"
            render={(isActive) => (
              <span>{isActive ? 'Sim' : 'Não'}</span>
            )}
            filters={[{
              text: 'Sim',
              value: true,
            },
            {
              text: 'Não',
              value: false,
            }]}
            filterMultiple={false}
            filteredValue={isActiveFilter !== null ? [isActiveFilter] : null}
          />
          <Table.Column
            title="Administrador"
            key="isAdmin"
            dataIndex="isAdmin"
            render={(isAdmin) => (
              <span>{isAdmin ? 'Sim' : 'Não'}</span>
            )}
            filters={[{
              text: 'Sim',
              value: true,
            },
            {
              text: 'Não',
              value: false,
            }]}
            filterMultiple={false}
            filteredValue={isAdminFilter !== null ? [isAdminFilter] : null}
          />
          <Table.Column
            title=""
            key="details"
            dataIndex="id"
            render={(userId) => (
              <DetailsButton onClick={() => breadcrumbNavigator.push({
                label: usersEntites[userId]?.name || 'Usuario',
                url: `${url}/${userId}`,
              })}
              >
                ver detalhes »
              </DetailsButton>
            )}
          />
        </Table>
      </MainContainer>
    </SectionBasePage>
  );
};

export default UserListPage;
